import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { UserCommunication } from '@this+that/models';

import { useShortcutsContext } from './ShortcutsContext';
import { ShortcutContext, ShortcutKey } from './types';
import { useEmailListActions } from './useEmailListActions';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';
import { useRemoveUserCommunication } from '../UserCommunications';
import { useCustomToast } from '../useCustomToast';

interface UseInboxSpamActionsProps {
  filteredCommunications: UserCommunication[];
  selectedItems: Set<string>;
  toggleItemSelection: (messageId: string) => void;
  clearSelection?: () => void;
}

function getSelectedEmail(
  filteredCommunications: UserCommunication[],
  selectedEmailIndex: number,
): UserCommunication | null {
  if (
    selectedEmailIndex >= 0 &&
    selectedEmailIndex < filteredCommunications.length
  ) {
    return filteredCommunications[selectedEmailIndex] ?? null;
  }
  return null;
}

export function useInboxSpamActions({
  filteredCommunications,
  selectedItems,
  toggleItemSelection,
  clearSelection,
}: UseInboxSpamActionsProps) {
  useEmailListActions({
    filteredCommunications,
    context: ShortcutContext.SPAM,
    selectedItems,
    toggleItemSelection,
    clearSelection,
  });

  const { registerShortcut, unregisterShortcut } = useKeyboardShortcuts();
  const { selectedEmailIndex } = useShortcutsContext();
  const { t } = useTranslation();
  const { promiseToast } = useCustomToast();
  const { handleRemoveUserCommunication } = useRemoveUserCommunication();

  useEffect(() => {
    const ctx = [ShortcutContext.SPAM];
    unregisterShortcut(ShortcutKey.DELETE, ctx);
    unregisterShortcut(ShortcutKey.DELETE_KEY, ctx);
    unregisterShortcut(ShortcutKey.BACKSPACE, ctx);

    const deleteHandler = () => {
      if (selectedItems.size > 0) {
        const itemsArray = Array.from(selectedItems);
        clearSelection?.();

        promiseToast(
          handleRemoveUserCommunication(itemsArray),
          t('inbox.userCommunication.actions.deleting'),
          t('inbox.userCommunication.actions.deleteMultipleSuccess', {
            count: itemsArray.length,
          }),
          t('inbox.userCommunication.actions.deleteError'),
        );
      } else {
        const selectedEmail = getSelectedEmail(
          filteredCommunications,
          selectedEmailIndex,
        );
        if (selectedEmail) {
          handleRemoveUserCommunication([selectedEmail.messageId]);
        }
      }
    };

    registerShortcut({
      key: ShortcutKey.DELETE,
      contexts: [ShortcutContext.SPAM],
      handler: deleteHandler,
      description: 'Delete selected spam email(s)',
    });

    registerShortcut({
      key: ShortcutKey.DELETE_KEY,
      contexts: [ShortcutContext.SPAM],
      handler: deleteHandler,
      description: 'Delete selected spam email(s)',
    });

    registerShortcut({
      key: ShortcutKey.BACKSPACE,
      contexts: [ShortcutContext.SPAM],
      handler: deleteHandler,
      description: 'Delete selected spam email(s)',
    });

    return () => {
      unregisterShortcut(ShortcutKey.DELETE, ctx);
      unregisterShortcut(ShortcutKey.DELETE_KEY, ctx);
      unregisterShortcut(ShortcutKey.BACKSPACE, ctx);
    };
  }, [
    registerShortcut,
    unregisterShortcut,
    selectedEmailIndex,
    filteredCommunications,
    selectedItems,
    clearSelection,
    handleRemoveUserCommunication,
    t,
    promiseToast,
  ]);
}
